import { useEffect, useRef, useState } from "react";
import type { ImApi } from "../domain/api";
import {
  microphoneErrorMessage,
  normalizedAudioCodec,
  recordingFileExtension,
  selectRecordingMimeType,
} from "../domain/audio";
import { imageFormat } from "../domain/messages";
import type { ContentType, SendDraft } from "../domain/types";
import { Icon } from "./Icon";

type AdvancedType = Exclude<ContentType, "text" | "image" | "audio">;

export type OutgoingPhase = "preparing" | "uploading" | "sending" | "failed";

export interface OutgoingActivity {
  /** Also sent as the protocol idempotency key for exact optimistic reconciliation. */
  readonly clientMsgId: string;
  readonly contentType: ContentType;
  readonly phase: OutgoingPhase;
  readonly progress: number;
  readonly previewUrl?: string;
  readonly previewText?: string;
  readonly error?: string;
}

export function Composer({
  api,
  disabled,
  onSend,
  onError,
  onActivity,
}: {
  readonly api: ImApi;
  readonly disabled: boolean;
  readonly onSend: (draft: SendDraft) => Promise<void>;
  readonly onError: (message: string) => void;
  readonly onActivity: (activity: OutgoingActivity | null) => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedType, setAdvancedType] = useState<AdvancedType>("custom");
  const [advancedPayload, setAdvancedPayload] = useState(templateFor("custom"));
  const [targetMessageId, setTargetMessageId] = useState("");
  const [mentionedUsers, setMentionedUsers] = useState("");
  const textInput = useRef<HTMLTextAreaElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const audioInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const recorderChunks = useRef<Blob[]>([]);
  const recorderStartedAt = useRef(0);
  const activity = useRef<OutgoingActivity | null>(null);

  useEffect(() => {
    const input = textInput.current;
    if (input === null) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
  }, [text]);

  async function sendText(): Promise<void> {
    const payload = text.trim();
    if (payload.length === 0 || busy) return;
    const clientMsgId = startActivity("text", payload);
    setBusy(true);
    try {
      await onSend({ clientMsgId, contentType: "text", payload });
      setText("");
      finishActivity();
    } catch (cause) {
      failActivity(cause);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  async function uploadImage(file: File): Promise<void> {
    const previewUrl = URL.createObjectURL(file);
    const activityId = startActivity("image", file.name, previewUrl, "preparing");
    setBusy(true);
    try {
      const bitmap = await createImageBitmap(file);
      const dimensions = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      updateActivity(activityId, { phase: "uploading", progress: 0 });
      const media = await api.upload(file, "image", file.name, (progress) => {
        updateActivity(activityId, { phase: "uploading", progress });
      });
      updateActivity(activityId, { phase: "sending", progress: 100 });
      await onSend({
        clientMsgId: activityId,
        contentType: "image",
        payload: JSON.stringify({
          uuid: crypto.randomUUID(),
          format: imageFormat(file.type),
          original: {
            objectKey: media.objectKey,
            sizeBytes: media.sizeBytes,
            ...dimensions,
          },
        }),
      });
      finishActivity(previewUrl);
    } catch (cause) {
      failActivity(cause, previewUrl);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  async function uploadAudio(file: Blob, name: string, durationMs?: number): Promise<void> {
    const activityId = startActivity("audio", name, undefined, "preparing");
    setBusy(true);
    try {
      const duration = durationMs ?? await audioDuration(file);
      updateActivity(activityId, { phase: "uploading", progress: 0 });
      const media = await api.upload(file, "audio", name, (progress) => {
        updateActivity(activityId, { phase: "uploading", progress });
      });
      updateActivity(activityId, { phase: "sending", progress: 100 });
      await onSend({
        clientMsgId: activityId,
        contentType: "audio",
        payload: JSON.stringify({
          uuid: crypto.randomUUID(),
          source: { objectKey: media.objectKey, sizeBytes: media.sizeBytes },
          durationMs: Math.max(1, Math.round(duration)),
          codec: normalizedAudioCodec(file.type),
          waveform: waveformFromDuration(duration),
        }),
      });
      finishActivity();
    } catch (cause) {
      failActivity(cause);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  async function uploadVideo(file: File): Promise<void> {
    const previewUrl = URL.createObjectURL(file);
    const activityId = startActivity("media-ref", file.name, previewUrl, "preparing");
    setBusy(true);
    try {
      const metadata = await videoMetadata(previewUrl);
      updateActivity(activityId, { phase: "uploading", progress: 0 });
      const media = await api.upload(file, "video", file.name, (progress) => {
        updateActivity(activityId, { phase: "uploading", progress });
      });
      updateActivity(activityId, { phase: "sending", progress: 100 });
      await onSend({
        clientMsgId: activityId,
        contentType: "media-ref",
        payload: JSON.stringify({
          version: 1,
          kind: "video",
          uuid: crypto.randomUUID(),
          source: { objectKey: media.objectKey, sizeBytes: media.sizeBytes },
          durationMs: metadata.durationMs,
          width: metadata.width,
          height: metadata.height,
          codec: file.type.split("/", 2)[1],
        }),
      });
      finishActivity(previewUrl);
    } catch (cause) {
      failActivity(cause, previewUrl);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  async function uploadFile(file: File): Promise<void> {
    const activityId = startActivity("media-ref", file.name, undefined, "preparing");
    setBusy(true);
    try {
      updateActivity(activityId, { phase: "uploading", progress: 0 });
      const uploadBody = file.type.length > 0
        ? file
        : file.slice(0, file.size, "application/octet-stream");
      const media = await api.upload(uploadBody, "file", file.name, (progress) => {
        updateActivity(activityId, { phase: "uploading", progress });
      });
      updateActivity(activityId, { phase: "sending", progress: 100 });
      await onSend({
        clientMsgId: activityId,
        contentType: "media-ref",
        payload: JSON.stringify({
          version: 1,
          kind: "file",
          uuid: crypto.randomUUID(),
          source: { objectKey: media.objectKey, sizeBytes: media.sizeBytes },
          fileName: file.name,
          extension: file.name.includes(".") ? file.name.split(".").at(-1) : undefined,
        }),
      });
      finishActivity();
    } catch (cause) {
      failActivity(cause);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  async function toggleRecording(): Promise<void> {
    if (recording) {
      recorder.current?.stop();
      setRecording(false);
      return;
    }
    if (!window.isSecureContext) {
      throw new Error("当前页面无法安全访问麦克风，请使用 HTTPS 打开");
    }
    const mediaDevices = Reflect.get(navigator, "mediaDevices") as MediaDevices | undefined;
    const MediaRecorderRuntime = Reflect.get(window, "MediaRecorder") as typeof MediaRecorder | undefined;
    if (mediaDevices === undefined || MediaRecorderRuntime === undefined) {
      throw new Error("当前浏览器不支持网页录音，请更新浏览器或使用音频文件上传");
    }

    let stream: MediaStream;
    try {
      stream = await mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (cause) {
      throw new Error(microphoneErrorMessage(cause), { cause });
    }

    let next: MediaRecorder;
    try {
      const mimeType = selectRecordingMimeType(
        MediaRecorderRuntime.isTypeSupported.bind(MediaRecorderRuntime),
      );
      next = mimeType === undefined
        ? new MediaRecorderRuntime(stream)
        : new MediaRecorderRuntime(stream, { mimeType });
    } catch (cause) {
      for (const track of stream.getTracks()) track.stop();
      throw new Error(microphoneErrorMessage(cause), { cause });
    }
    recorderChunks.current = [];
    recorderStartedAt.current = performance.now();
    let recorderFailed = false;
    next.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) recorderChunks.current.push(event.data);
    });
    next.addEventListener("stop", () => {
      for (const track of stream.getTracks()) track.stop();
      setRecording(false);
      if (recorderFailed) return;
      const duration = performance.now() - recorderStartedAt.current;
      const recordedType = next.mimeType.trim();
      const chunkType = recorderChunks.current[0]?.type.trim();
      const mimeType = recordedType.length > 0
        ? recordedType
        : chunkType !== undefined && chunkType.length > 0 ? chunkType : "audio/webm";
      const blob = new Blob(recorderChunks.current, { type: mimeType });
      if (blob.size === 0) {
        reportError(new Error("没有录到有效声音，请检查麦克风后重试"));
        return;
      }
      const extension = recordingFileExtension(mimeType);
      void uploadAudio(blob, `voice-${Date.now()}.${extension}`, duration).catch(reportError);
    });
    next.addEventListener("error", () => {
      recorderFailed = true;
      recorderChunks.current = [];
      for (const track of stream.getTracks()) track.stop();
      setRecording(false);
      reportError(new Error("录音过程中发生错误，请重试"));
    });
    recorder.current = next;
    next.start(250);
    setRecording(true);
  }

  async function sendAdvanced(): Promise<void> {
    let payload = advancedType === "retract" ? "" : advancedPayload;
    if (advancedType === "custom" || advancedType === "command") {
      payload = JSON.stringify(JSON.parse(advancedPayload) as unknown);
    }
    const mutation = advancedType === "edit" || advancedType === "retract";
    if (mutation && targetMessageId.trim().length === 0) {
      throw new Error("编辑或撤回消息必须填写目标 messageId");
    }
    const clientMsgId = startActivity(advancedType, contentTypeLabel(advancedType));
    setBusy(true);
    try {
      await onSend({
        clientMsgId,
        contentType: advancedType,
        payload,
        ...(mutation ? { targetMessageId: targetMessageId.trim() } : {}),
        ...(mentionedUsers.trim().length === 0
          ? {}
          : { mentionedUserIds: mentionedUsers.split(",").map((value) => value.trim()).filter(Boolean) }),
      });
      setShowAdvanced(false);
      setTargetMessageId("");
      setMentionedUsers("");
      finishActivity();
    } catch (cause) {
      failActivity(cause);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  function startActivity(
    contentType: ContentType,
    previewText: string,
    previewUrl?: string,
    phase: OutgoingPhase = "sending",
  ): string {
    if (activity.current?.previewUrl !== undefined) {
      URL.revokeObjectURL(activity.current.previewUrl);
    }
    const next: OutgoingActivity = {
      clientMsgId: crypto.randomUUID(),
      contentType,
      phase,
      progress: phase === "sending" ? 100 : 0,
      ...(previewUrl === undefined ? {} : { previewUrl }),
      ...(previewText.length === 0 ? {} : { previewText }),
    };
    activity.current = next;
    onActivity(next);
    return next.clientMsgId;
  }

  function updateActivity(id: string, patch: Partial<OutgoingActivity>): void {
    if (activity.current?.clientMsgId !== id) return;
    const next = { ...activity.current, ...patch };
    activity.current = next;
    onActivity(next);
  }

  function finishActivity(previewUrl?: string): void {
    const url = previewUrl ?? activity.current?.previewUrl;
    activity.current = null;
    onActivity(null);
    if (url !== undefined) URL.revokeObjectURL(url);
  }

  function failActivity(cause: unknown, previewUrl?: string): void {
    const current = activity.current;
    if (current === null) return;
    const message = cause instanceof Error ? cause.message : "消息发送失败";
    const failed = { ...current, phase: "failed" as const, error: message };
    activity.current = failed;
    onActivity(failed);
    window.setTimeout(() => {
      if (activity.current?.clientMsgId === failed.clientMsgId) finishActivity(previewUrl);
      else if (previewUrl !== undefined) URL.revokeObjectURL(previewUrl);
    }, 4_500);
  }

  /** Converts browser/media/JSON failures into a visible, actionable client notification. */
  function reportError(cause: unknown): void {
    onError(cause instanceof Error ? cause.message : "消息处理失败，请重试");
  }

  return (
    <div className="composer-wrap">
      {showAdvanced ? (
        <section className="advanced-composer" role="dialog" aria-label="更多消息格式">
          <header><div><span>全部消息格式</span><small>协议 v3</small></div>
            <button type="button" aria-label="关闭更多消息格式"
              onClick={() => { setShowAdvanced(false); }}><Icon name="close" /></button></header>
          <div className="advanced-grid">
            <label><span>格式</span><select value={advancedType} onChange={(event) => {
              const value = event.target.value as AdvancedType;
              setAdvancedType(value);
              setAdvancedPayload(templateFor(value));
            }}>
              <option value="custom">自定义消息</option><option value="command">CMD 指令</option>
              <option value="system">系统消息</option><option value="media-ref">媒体引用</option>
              <option value="ciphertext">密文</option><option value="edit">编辑消息</option>
              <option value="retract">撤回消息</option>
            </select></label>
            {advancedType === "edit" || advancedType === "retract" ? (
              <label><span>目标 messageId</span><input value={targetMessageId}
                onChange={(event) => { setTargetMessageId(event.target.value); }} placeholder="消息 ID" /></label>
            ) : null}
          </div>
          {advancedType === "retract" ? null : <label className="advanced-payload"><span>Payload</span>
            <textarea value={advancedPayload} onChange={(event) => { setAdvancedPayload(event.target.value); }} /></label>}
          <label className="advanced-mentions"><span>@ 用户 ID（逗号分隔，可选）</span>
            <input value={mentionedUsers} onChange={(event) => { setMentionedUsers(event.target.value); }} /></label>
          <footer><span>{advancedType === "system" ? "仅群主/管理员可发送系统消息" : "载荷上限 32 KiB"}</span>
            <button className="primary" type="button" disabled={busy || disabled}
              onClick={() => void sendAdvanced().catch(reportError)}>
              {busy ? "发送中…" : "发送该格式"}</button></footer>
        </section>
      ) : null}
      <div className="composer-actions" role="toolbar" aria-label="消息工具">
        <button type="button" disabled={disabled || busy} onClick={() => imageInput.current?.click()}
          aria-label="发送图片"><Icon name="image" /></button>
        <button className={recording ? "recording" : ""} type="button"
          disabled={(disabled || busy) && !recording} aria-pressed={recording}
          onClick={() => void toggleRecording().catch(reportError)}
          aria-label={recording ? "停止录音并发送" : "录制语音"}><Icon name="mic" /></button>
        <button type="button" disabled={disabled || busy} onClick={() => audioInput.current?.click()}
          aria-label="上传音频"><span className="tiny-type">MP3</span></button>
        <button type="button" disabled={disabled || busy} onClick={() => videoInput.current?.click()}
          aria-label="发送视频"><span className="tiny-type">视频</span></button>
        <button type="button" disabled={disabled || busy} onClick={() => fileInput.current?.click()}
          aria-label="发送文件"><span className="tiny-type">文件</span></button>
        <button type="button" disabled={disabled || busy} aria-expanded={showAdvanced}
          onClick={() => { setShowAdvanced((value) => !value); }} aria-label="更多消息格式"><Icon name="more" /></button>
        {recording ? <span className="recording-hint"><i /> 正在录音，点击麦克风发送</span> : null}
      </div>
      <div className="composer-input-row">
        <textarea ref={textInput} value={text} disabled={disabled || busy} rows={1}
          aria-label="输入消息" aria-describedby="composer-shortcut"
          placeholder={disabled ? "连接恢复后即可发送消息" : "输入消息"}
          onChange={(event) => { setText(event.target.value); }} onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              void sendText().catch(reportError);
            }
          }} />
        <button className="send-button" type="button" disabled={disabled || busy || text.trim().length === 0}
          aria-label={busy ? "正在发送" : "发送消息"}
          onClick={() => void sendText().catch(reportError)}><Icon name="send" /></button>
      </div>
      <div className="composer-footer" id="composer-shortcut">
        <span>{disabled ? "实时连接恢复后可继续发送" : "Enter 发送 · Shift + Enter 换行"}</span>
        <span>{text.length > 0 ? `${text.length} 字` : ""}</span>
      </div>
      <input ref={imageInput} hidden type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/avif"
        onChange={(event) => { const file = event.target.files?.[0]; if (file !== undefined) void uploadImage(file).catch(reportError); event.target.value = ""; }} />
      <input ref={audioInput} hidden type="file" accept="audio/*"
        onChange={(event) => { const file = event.target.files?.[0]; if (file !== undefined) void uploadAudio(file, file.name).catch(reportError); event.target.value = ""; }} />
      <input ref={videoInput} hidden type="file" accept="video/*"
        onChange={(event) => { const file = event.target.files?.[0]; if (file !== undefined) void uploadVideo(file).catch(reportError); event.target.value = ""; }} />
      <input ref={fileInput} hidden type="file"
        onChange={(event) => { const file = event.target.files?.[0]; if (file !== undefined) void uploadFile(file).catch(reportError); event.target.value = ""; }} />
    </div>
  );
}

function templateFor(type: AdvancedType): string {
  switch (type) {
    case "custom":
      return '{\n  "data": { "event": "card.click", "value": 1 },\n  "description": "业务自定义消息",\n  "extension": "sonox.demo"\n}';
    case "command":
      return '{\n  "name": "typing.start",\n  "version": 1,\n  "parameters": { "durationMs": 3000 },\n  "requireAck": false,\n  "priority": "low",\n  "pushEnabled": false\n}';
    case "system":
      return "群公告：欢迎加入会话";
    case "media-ref":
      return "media/external/reference";
    case "ciphertext":
      return "base64url-ciphertext";
    case "edit":
      return "编辑后的文本";
    case "retract":
      return "";
  }
}

async function audioDuration(blob: Blob): Promise<number> {
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<number>((resolve, reject) => {
      const audio = new Audio();
      audio.preload = "metadata";
      audio.addEventListener("loadedmetadata", () => { resolve(Math.max(1, audio.duration * 1_000)); }, { once: true });
      audio.addEventListener("error", () => { reject(new Error("无法读取音频时长")); }, { once: true });
      audio.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function videoMetadata(url: string): Promise<{
  readonly durationMs: number;
  readonly width: number;
  readonly height: number;
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.addEventListener("loadedmetadata", () => {
      resolve({
        durationMs: Math.max(1, Math.round(video.duration * 1_000)),
        width: Math.max(1, video.videoWidth),
        height: Math.max(1, video.videoHeight),
      });
    }, { once: true });
    video.addEventListener("error", () => { reject(new Error("无法读取视频信息")); }, { once: true });
    video.src = url;
  });
}

function waveformFromDuration(durationMs: number): number[] {
  const count = Math.max(8, Math.min(32, Math.round(durationMs / 250)));
  return Array.from({ length: count }, (_, index) => 32 + ((index * 47 + count * 13) % 190));
}

function contentTypeLabel(type: AdvancedType): string {
  const labels: Record<AdvancedType, string> = {
    custom: "自定义消息",
    command: "CMD 指令",
    system: "系统消息",
    "media-ref": "媒体引用",
    ciphertext: "密文消息",
    edit: "编辑消息",
    retract: "撤回消息",
  };
  return labels[type];
}
